import { useState } from 'react'
import { UseFormSetValue } from 'react-hook-form'
import { TNodeFormValues, TRange } from '../../../node.type'
import { createRange } from './range.helper'

const ANIMATION_DURATION = {
  BOUND_FLASH: 1200,
  SCALE_IN: 400,
} as const

interface UseRangeHandlersProps {
  ranges: TRange[]
  minValue: number
  maxValue: number
  setValue: UseFormSetValue<TNodeFormValues>
  updateRanges: (ranges: TRange[]) => void
}

type AutoUpdatedBounds = Array<{ rangeIndex: number; boundType: 'lower' | 'upper' }>

export function useRangeHandlers({
  ranges,
  minValue,
  maxValue,
  setValue,
  updateRanges: updateRangesInProvider,
}: UseRangeHandlersProps) {
  const [animatingIndex, setAnimatingIndex] = useState<number | null>(null)
  const [autoUpdatedBounds, setAutoUpdatedBounds] = useState<AutoUpdatedBounds>([])

  const updateRanges = (updatedRanges: TRange[]) => {
    setValue('variable.condition.ranges', updatedRanges, { shouldDirty: true })
    updateRangesInProvider(updatedRanges)
  }

  const triggerBoundAnimation = (bounds: AutoUpdatedBounds) => {
    setAutoUpdatedBounds(bounds)
    setTimeout(() => setAutoUpdatedBounds([]), ANIMATION_DURATION.BOUND_FLASH)
  }

  const triggerRangeAnimation = (rangeIndex: number) => {
    setAnimatingIndex(rangeIndex)
    setTimeout(() => setAnimatingIndex(null), ANIMATION_DURATION.SCALE_IN)
  }

  const handleLowerBoundChange = (rangeIndex: number, lowerBoundValue: number): void => {
    if (lowerBoundValue < minValue || lowerBoundValue > maxValue) {
      return
    }

    const updatedRanges = [...ranges]
    const currentRange = updatedRanges[rangeIndex]
    if (!currentRange) return

    updatedRanges[rangeIndex] = {
      ...currentRange,
      lowerBound: lowerBoundValue,
    }

    const prevRange = updatedRanges[rangeIndex - 1]
    if (prevRange) {
      const newPrevUpperBound = lowerBoundValue - 1

      if (newPrevUpperBound < prevRange.lowerBound) {
        updatedRanges.splice(rangeIndex - 1, 1)
      } else {
        updatedRanges[rangeIndex - 1] = {
          ...prevRange,
          upperBound: newPrevUpperBound,
        }
        triggerBoundAnimation([{ rangeIndex: rangeIndex - 1, boundType: 'upper' }])
      }
    }

    updateRanges(updatedRanges)
  }

  const handleUpperBoundChange = (rangeIndex: number, upperBoundValue: number): void => {
    if (upperBoundValue < minValue || upperBoundValue > maxValue) {
      return
    }

    const updatedRanges = [...ranges]
    const currentRange = updatedRanges[rangeIndex]
    if (!currentRange) return

    if (upperBoundValue < currentRange.lowerBound) {
      return
    }

    updatedRanges[rangeIndex] = {
      ...currentRange,
      upperBound: upperBoundValue,
    }

    if (upperBoundValue === maxValue) {
      updatedRanges.length = rangeIndex + 1
    } else {
      const nextRange = updatedRanges[rangeIndex + 1]
      if (nextRange) {
        const newNextLowerBound = upperBoundValue + 1

        if (!isNaN(nextRange.upperBound) && newNextLowerBound > nextRange.upperBound) {
          updatedRanges.splice(rangeIndex + 1, 1)
        } else {
          updatedRanges[rangeIndex + 1] = {
            ...nextRange,
            lowerBound: newNextLowerBound,
          }
          triggerBoundAnimation([{ rangeIndex: rangeIndex + 1, boundType: 'lower' }])
        }
      }
    }

    updateRanges(updatedRanges)

    const isLastRangeIndex = rangeIndex === updatedRanges.length - 1
    if (isLastRangeIndex && !isNaN(upperBoundValue) && upperBoundValue < maxValue) {
      const newRange =
        upperBoundValue === maxValue - 1
          ? createRange(maxValue, maxValue)
          : createRange(upperBoundValue + 1, NaN)

      const rangesWithNew = [...updatedRanges, newRange]
      updateRanges(rangesWithNew)
      triggerRangeAnimation(rangesWithNew.length - 1)
    }
  }

  const handleDeleteRange = (index: number): void => {
    const deletedRange = ranges[index]
    const wasLastRange = index === ranges.length - 1
    const hadMaxValue =
      deletedRange && !isNaN(deletedRange.upperBound) && deletedRange.upperBound === maxValue

    const updatedRanges = ranges.filter((_, i) => i !== index)

    if (updatedRanges.length === 0) {
      updateRanges([createRange(minValue, NaN)])
      return
    }

    if (updatedRanges.length === 1) {
      const lastRange = updatedRanges[0]
      if (!lastRange) return

      updatedRanges[0] = {
        ...lastRange,
        lowerBound: minValue,
        upperBound: NaN,
      }
      updateRanges(updatedRanges)
      return
    }

    const animatingBounds: AutoUpdatedBounds = []

    if (index < updatedRanges.length) {
      const prevRange = updatedRanges[index - 1]
      const rangeAfterDeleted = updatedRanges[index]

      if (prevRange && !isNaN(prevRange.upperBound) && rangeAfterDeleted) {
        updatedRanges[index] = {
          ...rangeAfterDeleted,
          lowerBound: prevRange.upperBound + 1,
        }
        animatingBounds.push({ rangeIndex: index, boundType: 'lower' })
      } else if (index === 0 && rangeAfterDeleted) {
        updatedRanges[0] = {
          ...rangeAfterDeleted,
          lowerBound: minValue,
        }
      }
    }

    if (wasLastRange && hadMaxValue) {
      const newLastIndex = updatedRanges.length - 1
      const newLastRange = updatedRanges[newLastIndex]
      if (newLastRange) {
        updatedRanges[newLastIndex] = {
          ...newLastRange,
          upperBound: maxValue,
        }
        animatingBounds.push({ rangeIndex: newLastIndex, boundType: 'upper' })
      }
    }

    updateRanges(updatedRanges)

    if (animatingBounds.length > 0) {
      triggerBoundAnimation(animatingBounds)
    }
  }

  const handleCreateAllRanges = (): void => {
    const allRanges: TRange[] = []
    for (let i = minValue; i <= maxValue; i++) {
      allRanges.push(createRange(i, i))
    }
    updateRanges(allRanges)
  }

  const handleAddRangeBetween = (rangeIndex: number): void => {
    const updatedRanges = [...ranges]
    const currentRange = updatedRanges[rangeIndex]
    const nextRange = updatedRanges[rangeIndex + 1]

    const canSplitCurrent =
      currentRange &&
      !isNaN(currentRange.upperBound) &&
      currentRange.upperBound - currentRange.lowerBound >= 1

    const canSplitNext =
      nextRange && !isNaN(nextRange.upperBound) && nextRange.upperBound - nextRange.lowerBound >= 1

    let rangeToSplit: TRange
    let indexToSplit: number

    if (canSplitCurrent) {
      rangeToSplit = currentRange
      indexToSplit = rangeIndex
    } else if (canSplitNext) {
      rangeToSplit = nextRange
      indexToSplit = rangeIndex + 1
    } else {
      return
    }

    const middle = Math.floor((rangeToSplit.lowerBound + rangeToSplit.upperBound) / 2)

    updatedRanges[indexToSplit] = {
      ...rangeToSplit,
      upperBound: middle,
    }

    const newRange = createRange(middle + 1, rangeToSplit.upperBound)
    updatedRanges.splice(indexToSplit + 1, 0, newRange)

    updateRanges(updatedRanges)

    triggerRangeAnimation(indexToSplit + 1)
    triggerBoundAnimation([
      { rangeIndex: indexToSplit, boundType: 'upper' },
      { rangeIndex: indexToSplit + 1, boundType: 'lower' },
    ])
  }

  return {
    animatingIndex,
    autoUpdatedBounds,
    handleLowerBoundChange,
    handleUpperBoundChange,
    handleDeleteRange,
    handleCreateAllRanges,
    handleAddRangeBetween,
  }
}
